// THE EDITOR'S « MODÈLES » — what a model does to the album's pages (design
// system v2, owner 2026-10-14: « I need to be able to use the new models in
// here, and the images that I want »). Pure: AlbumEditorPage calls these and
// puts the result in its state (undo-able, autosaved like any other edit);
// the dev harness and the tests call the same ones.
//
//   applyModel     — page N becomes the themed page the worker built
//                    (routes/themeEditor.ts): its old photos not in it go back
//                    to « non placées »; a photo a GRID page lent leaves it (the
//                    page closes up, as « Retirer » does); the client's own free
//                    elements stay; a single themed page in any album is fine —
//                    the book's structure does not change (only S1 makes a
//                    themed book: bookStructure.ts).
//   removeModel    — « Retirer le modèle »: back to a plain grid of its photos.
//   swapIntoWindow — a photo into a model's window (dropped, or « Changer la
//                    photo »): the face-safe window of it (themedWindow.swapCrop
//                    = the worker's faceSafeCrop), else centred + a warning.
//   mergeRedrawn   — the same model re-drawn by the worker (a photo removed):
//                    the words the client typed in its holders stay.

import { boxAspect, swapCrop, type WindowPhoto } from './decor/themedWindow';
import type { Crop } from './decor/cropMath';

export interface EditElement {
  id: string;
  type: string;
  width: number;
  height: number;
  photoId?: string;
  crop?: Crop;
  layer?: string;
  group?: string;
  captionFor?: string;
  holder?: string;
  text?: string;
}

export interface EditPage {
  page: number;
  layout: string;
  photos: string[];
  elements?: EditElement[];
  theme?: string;
  model?: string;
  blank?: boolean;
  template_id?: string | null;
  /** One half of a two-page model (S5 « Panorama »). */
  spreadHalf?: 'left' | 'right';
}

/** A themed page as the worker builds it (lib/themes/expand.ts ThemedPage). */
export interface BuiltPage {
  blank_color: string;
  photos: string[];
  background: { imageUrl?: string; color: string };
  elements: EditElement[];
  theme: string;
  model: string;
  colourway: string;
  side: 'left' | 'right';
  simple?: boolean;
  spreadHalf?: 'left' | 'right';
}

/** The grid layout for a page holding `n` photos. */
export function layoutForCount(n: number): '1-up' | '2-up' | '3-up' | '4-up' | '5-up' | '6-up' {
  return n <= 1 ? '1-up' : n === 2 ? '2-up' : n === 3 ? '3-up' : n === 4 ? '4-up' : n === 5 ? '5-up' : '6-up';
}

/** Fields a model's page carries that a plain page does not (removeModel drops them). */
const MODEL_FIELDS = ['theme', 'model', 'background', 'colourway', 'side', 'simple', 'banner', 'spreadHalf'] as const;
/** Fields a grid page carries that a model's page does not (applyModel drops them). */
const GRID_FIELDS = ['template_id', 'top_h_frac', 'fill', 'slot_positions'] as const;

/** An element the client added herself (no model layer), and not a caption tied to the old grid. */
const clientOwn = (e: EditElement) => !e.layer && !e.captionFor;

/** Is this element a photo WINDOW of a model (fixed box; the photo inside it is the client's)? */
export function isModelWindow(page: Pick<EditPage, 'theme'> | undefined, el: EditElement | undefined): boolean {
  return !!page?.theme && el?.type === 'photo' && (el.layer === 'photo' || (!el.layer && !!el.group));
}

export function applyModel<P extends EditPage>(pages: readonly P[], unassigned: readonly string[], pageIndex: number,
  built: BuiltPage, photoIds: readonly string[]): { pages: P[]; unassigned: string[] } {
  const page = pages[pageIndex];
  if (!page) return { pages: [...pages], unassigned: [...unassigned] };
  const assigned = new Set(photoIds.filter(Boolean));
  const released = page.photos.filter((id) => id && !assigned.has(id));
  const next = pages.map((p, i) => {
    if (i === pageIndex || !p.photos.some((id) => id && assigned.has(id))) return p;
    // a templated / blank page keeps its slots (an empty one is a drop target); a grid closes up
    if (p.template_id || p.blank) return { ...p, photos: p.photos.map((id) => (id && assigned.has(id) ? '' : id)) };
    const photos = p.photos.filter((id) => !(id && assigned.has(id)));
    return { ...p, photos, layout: layoutForCount(photos.filter(Boolean).length) };
  });
  const rest = { ...page } as unknown as Record<string, unknown>;
  for (const k of GRID_FIELDS) delete rest[k];
  next[pageIndex] = {
    ...rest,
    layout: '1-up', blank: true, blank_color: built.blank_color,
    photos: [...built.photos], background: built.background,
    elements: [...built.elements, ...(page.elements ?? []).filter(clientOwn)],
    theme: built.theme, model: built.model, colourway: built.colourway, side: built.side,
    ...(built.simple ? { simple: true } : {}),
  } as unknown as P;
  const out = unassigned.filter((id) => !assigned.has(id));
  for (const id of released) if (!out.includes(id) && !next.some((p) => p.photos.includes(id))) out.push(id);
  return { pages: next, unassigned: out };
}

/**
 * A TWO-PAGE model (S5 « Panorama », the picker's « Double page » — 2026-10-18):
 * album pages `leftIndex` and `leftIndex + 1` become its two halves (applyModel
 * on each). The photos of both pages not in the panorama go back to « non
 * placées »; the photo is listed by the left half only (placed once).
 */
export function applySpreadModel<P extends EditPage>(pages: readonly P[], unassigned: readonly string[], leftIndex: number,
  left: BuiltPage, right: BuiltPage, photoIds: readonly string[]): { pages: P[]; unassigned: string[] } {
  if (!pages[leftIndex] || !pages[leftIndex + 1]) return { pages: [...pages], unassigned: [...unassigned] };
  const a = applyModel(pages, unassigned, leftIndex, left, photoIds);
  const b = applyModel(a.pages, a.unassigned, leftIndex + 1, right, []);
  const out = b.pages.map((p, i) => (i === leftIndex ? { ...p, spreadHalf: 'left' } : i === leftIndex + 1 ? { ...p, spreadHalf: 'right' } : p) as P);
  return { pages: out, unassigned: b.unassigned };
}

/** The index of the other half of a two-page model, or -1. */
export function spreadMateIndex(pages: readonly EditPage[], index: number): number {
  const h = pages[index]?.spreadHalf;
  const j = h === 'left' ? index + 1 : h === 'right' ? index - 1 : -1;
  return j >= 0 && pages[j]?.spreadHalf && pages[j].spreadHalf !== h && pages[j].model === pages[index].model ? j : -1;
}

export function removeModel<P extends EditPage>(page: P): P {
  if (!page.theme) return page;
  const rest = { ...page } as unknown as Record<string, unknown>;
  for (const k of MODEL_FIELDS) delete rest[k];
  const photos = page.photos.filter(Boolean);
  return { ...rest, photos, layout: layoutForCount(photos.length), blank: photos.length === 0,
    elements: (page.elements ?? []).filter((e) => !e.layer) } as unknown as P;
}

/**
 * Put `newId` in the window `elementId` of page `pageNumber`. From this page's
 * other window → the two windows trade; dragged from another page's slot
 * (`source` 'slot') → the two places trade; from « Mes photos » (`sidebar`) a
 * photo placed elsewhere stays there too (the editor's « copy » rule). The
 * replaced photo goes back to « non placées » unless still placed. Null when
 * nothing changes.
 */
export function swapIntoWindow<P extends EditPage>(pages: readonly P[], unassigned: readonly string[], pageNumber: number,
  elementId: string, newId: string, source: 'slot' | 'sidebar', photoOf: (id: string) => WindowPhoto | null,
): { pages: P[]; unassigned: string[]; faceCut: boolean } | null {
  const pageIdx = pages.findIndex((p) => p.page === pageNumber);
  const page = pages[pageIdx];
  const el = page?.elements?.find((e) => e.id === elementId);
  if (!page || !el || el.type !== 'photo' || !newId) return null;
  // A panorama half: its window is half of ONE crop across the spine — another
  // photo goes in through « Modèles » → « Double page », which re-draws both halves.
  if (page.spreadHalf) return null;
  const oldId = el.photoId ?? '';
  if (newId === oldId) return null;
  const fitIn = (id: string, e: EditElement) => { const m = photoOf(id); return m ? swapCrop(m, boxAspect(e)) : { crop: undefined, faceCut: false }; };
  const fit = fitIn(newId, el);
  const next = pages.map((p) => ({ ...p, photos: [...p.photos], ...(p.elements ? { elements: [...p.elements] } : {}) }));
  const tgt = next[pageIdx];
  const twin = (tgt.elements ?? []).find((e) => e.id !== elementId && e.type === 'photo' && e.photoId === newId);
  tgt.elements = (tgt.elements ?? []).map((e) => {
    if (e.id === elementId) return { ...e, photoId: newId, crop: fit.crop };
    if (twin && e.id === twin.id) return { ...e, photoId: oldId || undefined, crop: oldId ? fitIn(oldId, e).crop : undefined };
    return e;
  });
  if (twin) {
    tgt.photos = tgt.photos.map((id) => (id === newId ? oldId : id === oldId ? newId : id));
  } else {
    tgt.photos = oldId && tgt.photos.includes(oldId) ? tgt.photos.map((id) => (id === oldId ? newId : id)) : [...tgt.photos.filter(Boolean), newId];
    if (source === 'slot') {
      for (let i = 0; i < next.length; i++) {
        if (i === pageIdx) continue;
        const sp = next[i];
        const k = sp.photos.indexOf(newId);
        if (k < 0) continue;
        if (sp.theme) {
          sp.elements = (sp.elements ?? []).map((e) => (e.type === 'photo' && e.photoId === newId
            ? { ...e, photoId: oldId || undefined, crop: oldId ? fitIn(oldId, e).crop : undefined } : e));
        }
        sp.photos[k] = oldId;
        break;
      }
    }
  }
  const out = unassigned.filter((id) => id !== newId);
  if (oldId && !out.includes(oldId) && !next.some((p) => p.photos.includes(oldId))) out.push(oldId);
  return { pages: next as P[], unassigned: out, faceCut: fit.faceCut };
}

/**
 * The same model re-drawn by the worker (a photo removed from it): the new
 * pieces and windows, the words the client typed in its holders (same role,
 * same order), her own free elements.
 */
export function mergeRedrawn<P extends EditPage>(page: P, redrawn: Pick<BuiltPage, 'photos' | 'background' | 'blank_color' | 'elements'>): P {
  const old = (page.elements ?? []).filter((e) => e.type === 'text' && e.holder);
  const seen = new Map<string, number>();
  const elements = redrawn.elements.map((e) => {
    if (e.type !== 'text' || !e.holder) return e;
    const k = seen.get(e.holder) ?? 0;
    seen.set(e.holder, k + 1);
    const was = old.filter((o) => o.holder === e.holder)[k];
    return was ? { ...e, text: was.text } : e;
  });
  return { ...page, photos: [...redrawn.photos], background: redrawn.background, blank_color: redrawn.blank_color,
    elements: [...elements, ...(page.elements ?? []).filter(clientOwn)] } as P;
}

// ─── « RÉGÉNÉRER » KEEPS HER ELEMENTS, NEVER THE OLD MODEL'S (#WA-510802) ────
//
// 2026-10-14, 22:45. The owner pressed « Régénérer » on a mariage album. The
// worker sent back fresh pages — page 5 now model 2-04 with photos [1ed4, c133]
// and its OWN two windows. The editor re-attached « the client's elements » by
// page NUMBER and wrote them OVER the fresh page's elements: page 5 kept the
// OLD 1-13 page's paper, one window (1ed4) and holders, so c133 sat in
// `photos` with no window and the print refused; page 4, a plain page now,
// kept the old 1-09 window and drew c133 over its own photo. Fifteen pages of
// that album (and #1210, #D374) came out that way.
//
// The rule: a model's pieces (anything with a `layer`: paper, windows, frames,
// holders…) belong to the model that drew them and leave with it. Only her own
// elements (no layer: free text, shapes, captions) follow the page number, and
// they are added AFTER the fresh page's own elements, never instead of them.

/** An element that follows its page through « Régénérer »: hers, not a model's piece. */
export const carriedThroughRegen = (e: Pick<EditElement, 'layer'>): boolean => !e.layer;

/**
 * Re-attach the client's own elements of `prev` onto the `fresh` layout by page
 * number; a page that no longer exists hands them to the nearest survivor. The
 * fresh page's own elements (a model's paper / windows / holders) stay first.
 */
export function reattachAfterRegen<P extends EditPage>(prev: readonly EditPage[], fresh: readonly P[]): P[] {
  const own = new Map<number, EditElement[]>();
  for (const p of prev) {
    const els = (p.elements ?? []).filter(carriedThroughRegen);
    if (els.length) own.set(p.page, [...(own.get(p.page) ?? []), ...els]);
  }
  const nums = fresh.map((p) => p.page);
  const extra = new Map<number, EditElement[]>();
  for (const [n, els] of own) {
    if (nums.includes(n) || nums.length === 0) continue;
    let nearest = nums[0];
    for (const sp of nums) if (Math.abs(sp - n) < Math.abs(nearest - n)) nearest = sp;
    extra.set(nearest, [...(extra.get(nearest) ?? []), ...els]);
  }
  return fresh.map((p) => {
    const carried = [...(own.get(p.page) ?? []), ...(extra.get(p.page) ?? [])];
    return carried.length ? { ...p, elements: [...(p.elements ?? []), ...carried] } : p;
  });
}

// ─── A MODEL PAGE THAT DISAGREES WITH ITSELF IS PRINTED AS A GRID ────────────
//
// The defensive half (render + editor load). A model page (themed, blank) draws
// its photos ONLY through its windows. When `photos` and the windows disagree,
// some photo is either not printed at all (in `photos`, no window — the print
// refuses) or printed twice (a window of a photo `photos` gave to another page).
// `photos` is the truth — it is what the layout placed, each photo once — so the
// page becomes the plain grid of its `photos` (« Retirer le modèle »), her own
// elements kept. Every photo prints, none twice; the page loses its décor, which
// a « Régénérer » or « Appliquer un modèle » puts back. Failing the print instead
// would block an order on a page nobody can see is wrong in the editor.
// A plain page carrying a model's pieces (a stray window from an old model) loses
// them the same way.

function windowIds(page: EditPage): string[] {
  return (page.elements ?? []).filter((e) => isModelWindow(page, e) && e.photoId).map((e) => e.photoId as string);
}

/** Does this page contradict itself (its listed photos vs the photos its windows draw)? */
export function modelPageMismatch(page: EditPage): boolean {
  if (page.theme) {
    if (!page.blank) return false; // a composer page on the theme's paper: slots, not windows
    const listed = new Set(page.photos.filter(Boolean));
    const shown = new Set(windowIds(page));
    // S5's RIGHT half draws the photo its LEFT half lists (one photo, placed once —
    // worker spread.ts): its window with no photo of its own is the design, not a fault.
    if (page.spreadHalf === 'right' && listed.size === 0 && shown.size <= 1) return false;
    if (listed.size !== shown.size) return true;
    for (const id of listed) if (!shown.has(id)) return true;
    return false;
  }
  return (page.elements ?? []).some((e) => !!e.layer);
}

/** Repair every self-contradicting page (see above). Same array back when nothing was wrong. */
export function repairModelPages<P extends EditPage>(pages: readonly P[]): { pages: P[]; repaired: number[] } {
  const repaired: number[] = [];
  const out = pages.map((p) => {
    if (!modelPageMismatch(p)) return p;
    repaired.push(p.page);
    if (p.theme) return removeModel(p);
    return { ...p, elements: (p.elements ?? []).filter((e) => !e.layer) };
  });
  return repaired.length ? { pages: out, repaired } : { pages: pages as P[], repaired };
}
